import { Layout, Menu, Typography } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  FileTextOutlined,
  AuditOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSettingsStore, COLOR_THEMES } from '@store/settingsStore';
import { usePermissions } from '@hooks/usePermissions';
import { APP_NAME } from '@/utils';
import { PERMISSIONS } from '@/types';
import { ROUTES } from '@app/router/routes';

export function Sidebar() {
  const collapsed = useSettingsStore((s) => s.sidebarCollapsed);
  const theme = useSettingsStore((s) => s.theme);
  const colorTheme = useSettingsStore((s) => s.colorTheme);
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const activeColor = COLOR_THEMES.find(t => t.key === colorTheme)?.color || '#2563eb';

  const items = [
    { key: ROUTES.dashboards, icon: <DashboardOutlined />, label: 'Dashboards' },
    can(PERMISSIONS.REPORTS_VIEW) && {
      key: ROUTES.reports,
      icon: <FileTextOutlined />,
      label: 'Reports',
    },
    can(PERMISSIONS.USERS_MANAGE) && {
      key: 'admin',
      icon: <SafetyCertificateOutlined />,
      label: 'Admin',
      children: [
        { key: ROUTES.users, icon: <UserOutlined />, label: 'Users' },
        can(PERMISSIONS.ROLES_MANAGE) && {
          key: ROUTES.roles,
          icon: <SafetyCertificateOutlined />,
          label: 'Roles',
        },
        can(PERMISSIONS.ROLES_MANAGE) && {
          key: ROUTES.permissions,
          icon: <KeyOutlined />,
          label: 'Permissions',
        },
        can(PERMISSIONS.AUDIT_VIEW) && {
          key: ROUTES.audit,
          icon: <AuditOutlined />,
          label: 'Audit Logs',
        },
      ].filter(Boolean),
    },
    { key: ROUTES.settings, icon: <SettingOutlined />, label: 'Settings' },
  ].filter(Boolean) as { key: string }[];

  return (
    <Layout.Sider collapsible collapsed={collapsed} trigger={null} width={240}>
      <div
        style={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 12,
          borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)',
          transition: 'all 0.3s',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${activeColor}, ${activeColor}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#white',
            fontWeight: 'bold',
            fontSize: 16,
            boxShadow: `0 4px 10px ${activeColor}40`,
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#ffffff', fontFamily: 'Inter, sans-serif' }}>{APP_NAME.charAt(0)}</span>
        </div>
        {!collapsed && (
          <Typography.Title
            level={5}
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: '-0.3px',
              color: theme === 'dark' ? '#ffffff' : '#0f172a',
            }}
          >
            {APP_NAME}
          </Typography.Title>
        )}
      </div>
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={['admin']}
        items={items as any}
        onClick={(e) => {
          if (typeof e.key === 'string' && e.key.startsWith('/')) navigate(e.key);
        }}
        style={{ borderInlineEnd: 'none', paddingTop: 8 }}
      />
    </Layout.Sider>
  );
}
